import React from 'react';
import { StudyPlanStep } from '../../types/study-plan';
import './ProgressSidebar.css';

interface ProgressSidebarProps {
  steps: StudyPlanStep[];
  currentStep: number;
  onStepClick?: (stepNumber: number) => void;
}

const ProgressSidebar: React.FC<ProgressSidebarProps> = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className="progress-sidebar bg-light border-end">
      <div className="sidebar-header p-4 border-bottom">
        <h5 className="mb-0 text-primary fw-bold">Study Plan Setup</h5>
        <small className="text-white">Complete all steps to generate your plan</small>
      </div>
      
      <div className="steps-container p-3">
        {steps.map((step, index) => {
          const isClickable = step.completed || step.current;
          
          return (
            <div 
              key={step.id} 
              className={`step-item d-flex align-items-center mb-3 ${
                step.completed ? 'completed' : 
                step.current ? 'current' : 'pending'
              } ${isClickable && onStepClick ? 'clickable' : ''}`}
              onClick={() => isClickable && onStepClick?.(step.id)}
              style={{ 
                cursor: isClickable && onStepClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (isClickable && onStepClick) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable && onStepClick) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
            <div className="step-indicator me-3">
              {step.completed ? (
                <div className="step-circle completed">
                  <i className="fas fa-check text-white"></i>
                </div>
              ) : step.current ? (
                <div className="step-circle current">
                  <span className="text-white fw-bold">{step.id}</span>
                </div>
              ) : (
                <div className="step-circle pending">
                  <span className="text-muted">{step.id}</span>
                </div>
              )}
            </div>
            
            <div className="step-content flex-grow-1">
              <h6 className={`mb-1 ${step.completed ? 'text-success' : step.current ? 'text-primary' : 'text-muted'}`}>
                {step.title}
              </h6>
              <small className="text-muted d-block">{step.description}</small>
            </div>
            
            {index < steps.length - 1 && (
              <div className="step-connector"></div>
            )}
          </div>
          );
        })}
      </div>
      
      <div className="sidebar-footer p-3 border-top mt-auto">
        <div className="progress mb-2">
          <div 
            className="progress-bar bg-primary" 
            role="progressbar" 
            style={{ width: `${(steps.filter(s => s.completed).length / steps.length) * 100}%` }}
          ></div>
        </div>
        <small className="text-muted">
          {steps.filter(s => s.completed).length} of {steps.length} steps completed
        </small>
      </div>
    </div>
  );
};

export default ProgressSidebar;